import * as THREE from "three";
import { SHAFT_SPREAD } from "./lightShaftGeometry";
import type { LightShaftPlacement } from "./lightShaftLayout";

/**
 * Dust motes hanging in the evening light shafts.
 *
 * The motes live in the SAME local space as the shaft frustum
 * (`buildLightShaftFrustum`): X along the wall, Z up, Y running from 0 at
 * the glass to -1 at the far end, stretched to metres by the mesh's Y
 * scale. So a `<points>` given the beam's position/quaternion/scale fills
 * exactly that beam, and every mote stays inside the light that makes it
 * visible. Motes outside a beam would be invisible in reality: the whole
 * point is that dust only shows where light crosses it.
 */

/** Motes per square metre of glazing. */
const MOTES_PER_M2 = 24;
/** A small window still needs enough motes to read as a haze of dust. */
const MIN_MOTES_PER_SHAFT = 10;
const MAX_MOTES_PER_SHAFT = 90;
/** Keep motes off the beam's fresnel-bright walls. */
const CROSS_SECTION_FILL = 0.82;

function hash01(seed: number, salt: number): number {
	const x = Math.sin(seed * 127.1 + salt * 311.7) * 43758.5453;
	return x - Math.floor(x);
}

export function getShaftDustCount(placement: LightShaftPlacement): number {
	return THREE.MathUtils.clamp(
		Math.round(placement.widthM * placement.heightM * MOTES_PER_M2),
		MIN_MOTES_PER_SHAFT,
		MAX_MOTES_PER_SHAFT,
	);
}

/**
 * Deterministic scatter: the same opening always gets the same motes, so
 * a still frame is reproducible (visual baselines, the landing film).
 */
export function buildShaftDustGeometry(
	placement: LightShaftPlacement,
): THREE.BufferGeometry {
	const count = getShaftDustCount(placement);
	const halfW = placement.widthM / 2;
	const halfH = placement.heightM / 2;
	const positions = new Float32Array(count * 3);
	const seeds = new Float32Array(count);
	for (let i = 0; i < count; i += 1) {
		// Biased toward the glass, where the beam is brightest.
		const along = hash01(i, 1) ** 1.35;
		const spread = 1 + (SHAFT_SPREAD - 1) * along;
		positions[i * 3] =
			(hash01(i, 2) * 2 - 1) * halfW * spread * CROSS_SECTION_FILL;
		positions[i * 3 + 1] = -along;
		positions[i * 3 + 2] =
			(hash01(i, 3) * 2 - 1) * halfH * spread * CROSS_SECTION_FILL;
		seeds[i] = hash01(i, 4);
	}
	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
	geometry.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 1));
	geometry.computeBoundingSphere();
	return geometry;
}

export const SHAFT_DUST_VERTEX_SHADER = /* glsl */ `
uniform float uTime;
uniform float uPixelSize;
attribute float aSeed;
varying float vSeed;
varying float vAlong;

void main() {
	vec3 p = position;
	// Slow Brownian-looking drift, a few centimetres, plus a lazy rise.
	// X/Z are metres; Y is the unit length axis, so it barely moves.
	p.x += sin(uTime * 0.21 + aSeed * 40.0) * 0.035 + sin(uTime * 0.57 + aSeed * 13.0) * 0.012;
	p.z += sin(uTime * 0.16 + aSeed * 23.0) * 0.03 + sin(uTime * 0.12 + aSeed * 6.28) * 0.04;
	vec4 mv = modelViewMatrix * vec4(p, 1.0);
	gl_Position = projectionMatrix * mv;
	// Perspective size, clamped so near motes never become blobs and far
	// ones never drop below a pixel (which would flicker as they drift).
	gl_PointSize = clamp(uPixelSize * (0.55 + aSeed * 0.9) / max(-mv.z, 0.1), 0.6, 2.5);
	vSeed = aSeed;
	vAlong = -position.y;
}
`;

export const SHAFT_DUST_FRAGMENT_SHADER = /* glsl */ `
uniform float uTime;
uniform float uIntensity;
uniform vec3 uColor;
varying float vSeed;
varying float vAlong;

void main() {
	float d = length(gl_PointCoord - 0.5);
	float mote = 1.0 - smoothstep(0.08, 0.5, d);
	// Motes turn as they drift and catch the light only now and then.
	float glint = 0.55 + 0.2 * sin(uTime * 0.3 + vSeed * 60.0);
	// Same fade as the beam itself: brightest at the glass, gone at the end.
	float fade = (1.0 - vAlong) * smoothstep(0.0, 0.06, vAlong);
	gl_FragColor = vec4(uColor, clamp(mote * glint * fade * uIntensity * 0.3, 0.0, 1.0));
}
`;
